import React, { useCallback, useState } from 'react';

import isString from 'lodash/isString';
import { observer } from 'mobx-react-lite';

import { Box, Button, Divider, Text } from '@r-client/shared/ui/core';

import { TGetEventsEvent } from '../../types';
import { EventCard } from '../event-card/event-card';
import { RsvpModal } from '../rsvp-modal/rsvp-modal';

import styles from './event-list.module.scss';

export interface IEventListProps {
  events: TGetEventsEvent[];
  title: string | React.ReactElement;
  loading: boolean;
  onLoadMore: () => void;
  pageInfo?: {
    hasNextPage: boolean;
  };
}

const headerM = {
  xs: '0 0 1brm 0',
  sm: '0 0 3.5brm 0',
  md: '0 0 3.5brm 0',
  lg: '0 0 3.5brm 0',
} as const;

const dividerMargin = {
  top: { xs: '1.5', sm: '3.5', md: '3.5', lg: '3.5' },
  bottom: { xs: '1.5', sm: '1.5', md: '1.5', lg: '1.5' },
} as const;

const openNewWindow = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const EventList: React.FC<React.PropsWithChildren<IEventListProps>> =
  observer(function EventListDisplayer({
    title,
    events,
    pageInfo,
    onLoadMore,
    loading,
  }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEventId, setSelectedEventId] = useState('');
    const closeRsvpModal = useCallback(() => {
      setIsModalOpen(false);
    }, []);

    const openRsvpModalForEvent = useCallback((eventId: string) => {
      setSelectedEventId(eventId);
      setIsModalOpen(true);
    }, []);

    const selectedEvent = events.find((event) => event.id === selectedEventId);

    const titleElement = isString(title) ? (
      <Text color="gray5" type="h4" as="h4">
        {title}
      </Text>
    ) : (
      title
    );

    return (
      <section>
        <Box as="header" m={headerM}>
          {titleElement}
        </Box>

        <div>
          {events.map((event, index) => (
            <React.Fragment key={event.id}>
              <EventCard
                event={event}
                onWindowOpen={openNewWindow}
                onModalOpen={openRsvpModalForEvent}
              />
              {index + 1 !== events.length && (
                <Divider margin={dividerMargin} />
              )}
            </React.Fragment>
          ))}
        </div>

        {!!pageInfo?.hasNextPage && (
          <Box m="3brm 0 0 0" className={styles.loadMoreWrapper}>
            <Button onClick={onLoadMore} wide loading={loading}>
              View more
            </Button>
          </Box>
        )}

        {!!selectedEvent && (
          <RsvpModal
            event={selectedEvent}
            isModalOpen={isModalOpen}
            onCloseModal={closeRsvpModal}
          />
        )}
      </section>
    );
  });
